import { noise } from '@chainsafe/libp2p-noise';
import { yamux } from '@chainsafe/libp2p-yamux';
import { bootstrap } from '@libp2p/bootstrap';
import { floodsub, type Message, type SubscriptionChangeData } from '@libp2p/floodsub';
import { KEEP_ALIVE, type Libp2pEvents, type PeerId } from '@libp2p/interface';
import { identify } from '@libp2p/identify';
import { ping } from '@libp2p/ping';
import { webSockets } from '@libp2p/websockets';
import { createLibp2p, type ConnectionManagerInit } from 'libp2p';

import { P2P_CONFIG } from '@/config/p2pConfig';
import { Libp2pService } from '@/services/abstract/poc/Libp2pService';
import type { LoggerService } from '@/services/abstract/LoggerService';
import { extractConnectionInfo, isInfraPeer } from '@/services/poc/libp2p.utils';
import type { Logger } from '@/types/Logger';
import type { LibP2pWithServices, NodeOptions } from '@/types/poc/libp2p';
import { getErrorMessage } from '@/utils/error.utils';

// Per-`createNode` data the event handlers need. The service stays stateless; this is built
// once per node and passed to each handler so they can reach the node, its callbacks, and the
// bootstrap list (for `isInfraPeer`) without instance state.
interface NodeEventContext {
  node: LibP2pWithServices;
  callbacks: NonNullable<NodeOptions['callbacks']>;
  bootstrapNodes: string[];
}

export class Libp2pServiceImpl extends Libp2pService {
  private readonly log: Logger;

  constructor(loggerService: LoggerService) {
    super();
    this.log = loggerService.createLogger({
      namespace: P2P_CONFIG.logging.namespace.libp2p,
    });
  }

  async createNode(options: NodeOptions): Promise<LibP2pWithServices> {
    const uniqueBootstrapPeers = [...new Set(options.bootstrapNodes)].filter(
      (bootstrapPeer) => bootstrapPeer !== ''
    );

    const callbacks = options.callbacks ?? {};

    if (options.turnConfig != null) {
      this.log.info('Using pre-generated TURN credentials', {
        urls: options.turnConfig.urls,
        username: options.turnConfig.username,
      });
    } else {
      this.log.warn('No TURN config provided, WebRTC may fail without ICE servers');
    }

    // `clientStreamWindowSize`/`maxStreamWindowSize` (root) and the nested stream-count
    // overrides are runtime-valid yamux tunables absent from its exported init type — annotated
    // here so the config stays type-checked without a blanket `as any`.
    const yamuxMuxerInit: NonNullable<Parameters<typeof yamux>[0]> & {
      clientStreamWindowSize: number;
      maxStreamWindowSize: number;
      streamOptions: { maxInboundStreams: number; maxOutboundStreams: number };
    } = {
      maxInboundStreams: P2P_CONFIG.yamux.maxInboundStreams,
      maxOutboundStreams: P2P_CONFIG.yamux.maxOutboundStreams,
      enableKeepAlive: P2P_CONFIG.yamux.enableKeepAlive,
      keepAliveInterval: P2P_CONFIG.yamux.keepAliveInterval,
      clientStreamWindowSize: P2P_CONFIG.yamux.clientStreamWindowSize,
      maxStreamWindowSize: P2P_CONFIG.yamux.maxStreamWindowSize,
      streamOptions: {
        initialStreamWindowSize: P2P_CONFIG.yamux.initialStreamWindowSize,
        maxStreamWindowSize: P2P_CONFIG.yamux.maxStreamWindowSize,
        maxInboundStreams: P2P_CONFIG.yamux.maxInboundStreams,
        maxOutboundStreams: P2P_CONFIG.yamux.maxOutboundStreams,
      },
    };

    // `autoDialInterval` is a runtime-valid field absent from the exported
    // `ConnectionManagerInit`; the intersection keeps the rest of the config type-checked.
    const connectionManagerInit: ConnectionManagerInit & { autoDialInterval: number } = {
      maxConnections: P2P_CONFIG.connectionManager.maxConnections,
      maxPeerAddrsToDial: P2P_CONFIG.connectionManager.maxPeerAddrsToDial,
      inboundConnectionThreshold: P2P_CONFIG.connectionManager.inboundConnectionThreshold,
      autoDialInterval: P2P_CONFIG.connectionManager.autoDialInterval,
      dialTimeout: P2P_CONFIG.connectionManager.dialTimeout,
      reconnectRetries: P2P_CONFIG.connectionManager.reconnectRetries,
      reconnectRetryInterval: P2P_CONFIG.connectionManager.reconnectRetryInterval,
      reconnectBackoffFactor: P2P_CONFIG.connectionManager.reconnectBackoffFactor,
    };

    const node = await createLibp2p({
      addresses: {
        listen: [],
        announce: [],
      },
      transports: [webSockets()],
      connectionEncrypters: [noise()],
      streamMuxers: [yamux(yamuxMuxerInit)],
      connectionGater: {
        denyDialMultiaddr: () => false,
      },
      connectionManager: connectionManagerInit,
      connectionMonitor: {
        enabled: false,
      },
      services: {
        identify: identify(),
        ping: ping({
          timeout: P2P_CONFIG.ping.timeout,
        }),
        pubsub: floodsub(),
        bootstrap: bootstrap({
          list: uniqueBootstrapPeers,
        }),
      },
    });

    await node.start();

    this.setupEventListeners(node, callbacks, uniqueBootstrapPeers);

    return node;
  }

  async stopNode(node: LibP2pWithServices): Promise<void> {
    const peerId = node.peerId.toString();
    this.log.info(`Stopping LibP2P node: ${peerId}...`);

    try {
      await node.stop();
      this.log.info(`LibP2P node stopped: ${peerId}...`);
    } catch (error) {
      this.log.error(`Error stopping LibP2P node ${peerId}...:`, error);
      throw error;
    }
  }

  subscribeToTopic(
    node: LibP2pWithServices,
    topic: string,
    handler: (message: Uint8Array, originPeerId: string | null) => void
  ): () => void {
    this.log.debug(`LibP2P subscribing to topic: ${topic}`);
    node.services.pubsub.subscribe(topic);

    const eventHandler = (event: CustomEvent<Message>): void => {
      if (event.detail.topic === topic) {
        // StrictSign (pubsub default) guarantees signed messages, so `from` is the
        // cryptographically verified origin peer; unsigned would carry no `from`.
        const originPeerId = event.detail.type === 'signed' ? event.detail.from.toString() : null;
        handler(event.detail.data, originPeerId);
      }
    };

    node.services.pubsub.addEventListener('message', eventHandler);

    return () => {
      this.log.debug(`Unsubscribing from topic: ${topic}`);
      node.services.pubsub.removeEventListener('message', eventHandler);
      node.services.pubsub.unsubscribe(topic);
    };
  }

  private setupEventListeners(
    node: LibP2pWithServices,
    callbacks: NonNullable<NodeOptions['callbacks']>,
    bootstrapNodes: string[]
  ): void {
    const context: NodeEventContext = { node, callbacks, bootstrapNodes };

    node.addEventListener('connection:open', (event) => {
      this.handleConnectionOpen(event, context);
    });
    node.addEventListener('connection:close', (event) => {
      this.handleConnectionClose(event, context);
    });
    node.addEventListener('peer:connect', (event) => {
      this.handlePeerConnect(event, context);
    });
    node.addEventListener('peer:reconnect-failure', (event) => {
      this.handlePeerReconnectFailure(event, context);
    });
    node.addEventListener('peer:disconnect', (event) => {
      this.handlePeerDisconnect(event, context);
    });
    node.addEventListener('peer:discovery', (event) => {
      this.handlePeerDiscovery(event, context);
    });
    node.services.pubsub.addEventListener('subscription-change', (event) => {
      this.handleSubscriptionChange(event, context);
    });
  }

  private handleConnectionOpen(
    event: Libp2pEvents['connection:open'],
    context: NodeEventContext
  ): void {
    const connectionInfo = extractConnectionInfo(event.detail);

    if (connectionInfo.isRelay) {
      this.log.info('RELAY CONNECTION ESTABLISHED');
      this.log.info(`Relay: ${connectionInfo.peerId}`);
    }

    context.callbacks.onConnectionOpen?.(connectionInfo);
  }

  private handleConnectionClose(
    event: Libp2pEvents['connection:close'],
    context: NodeEventContext
  ): void {
    const { node, callbacks, bootstrapNodes } = context;
    const connectionInfo = extractConnectionInfo(event.detail);
    if (isInfraPeer(connectionInfo.peerId, bootstrapNodes)) {
      const durationMs = Date.now() - event.detail.timeline.open;
      this.log.info(
        `Infra peer connection closed: ${connectionInfo.peerId} ` +
          `(direction=${connectionInfo.direction}, durationMs=${durationMs}, ` +
          `remaining=${node.getConnections().length})`
      );
    } else {
      this.log.debug(`Connection closed: ${connectionInfo.peerId}`);
    }
    callbacks.onConnectionClose?.(connectionInfo);
  }

  private handlePeerConnect(event: Libp2pEvents['peer:connect'], context: NodeEventContext): void {
    const { node, callbacks, bootstrapNodes } = context;
    const peerId = event.detail.toString();
    const connections = node.getConnections(event.detail);

    this.log.debug(`Peer connected: ${peerId} (${connections.length} connections)`);

    const connectionInfos = connections.map(extractConnectionInfo);
    callbacks.onPeerConnect?.(peerId, connectionInfos);

    if (isInfraPeer(peerId, bootstrapNodes)) {
      this.log.info(`Connected to bootstrap peer (HAP): ${peerId}`);
      this.log.info('Will re-announce subscriptions after pubsub streams establish...');

      node.peerStore
        .merge(event.detail, {
          tags: { [KEEP_ALIVE]: { value: P2P_CONFIG.keepAlive.tagValue } },
        })
        .then(() => {
          this.log.debug(`Tagged infra peer keep-alive: ${peerId}`);
        })
        .catch((error: unknown) => {
          this.log.debug(`Failed to tag keep-alive ${peerId}: ${getErrorMessage(error)}`);
        });

      setTimeout(() => {
        this.reannounceSubscriptions(node, peerId);
      }, P2P_CONFIG.reannounce.delay);
    }
  }

  private handlePeerReconnectFailure(
    event: Libp2pEvents['peer:reconnect-failure'],
    context: NodeEventContext
  ): void {
    const peerIdObject = event.detail;
    const peerId = peerIdObject.toString();
    if (!isInfraPeer(peerId, context.bootstrapNodes)) {
      return;
    }

    this.log.warn(`Infra peer reconnect exhausted (keep-alive stripped): ${peerId} — re-arming`);
    this.rearmInfraPeer(context.node, peerIdObject);
  }

  private handlePeerDisconnect(
    event: Libp2pEvents['peer:disconnect'],
    context: NodeEventContext
  ): void {
    const peerId = event.detail.toString();
    this.log.debug(`Peer disconnected: ${peerId}`);
    context.callbacks.onPeerDisconnect?.(peerId);
  }

  private handlePeerDiscovery(
    event: Libp2pEvents['peer:discovery'],
    context: NodeEventContext
  ): void {
    const { node, bootstrapNodes } = context;
    const discoveredPeer = event.detail;
    const peerId = discoveredPeer.id.toString();

    if (isInfraPeer(peerId, bootstrapNodes) || peerId === node.peerId.toString()) {
      return;
    }

    const existingConnection = node.getConnections(discoveredPeer.id);
    if (existingConnection.length > 0) {
      return;
    }

    this.log.debug(`Discovered peer ${peerId}, attempting to dial...`);
    node
      .dial(discoveredPeer.id)
      .then(() => {
        this.log.debug(`Successfully dialed ${peerId}`);
      })
      .catch((error: unknown) => {
        this.log.debug(`Failed to dial ${peerId}: ${getErrorMessage(error)}`);
      });
  }

  private handleSubscriptionChange(
    event: CustomEvent<SubscriptionChangeData>,
    context: NodeEventContext
  ): void {
    const peerId = event.detail.peerId.toString();
    const subscriptions = event.detail.subscriptions.map((subscription) => ({
      topic: subscription.topic,
      subscribe: subscription.subscribe,
    }));

    this.log.debug(`Subscription change from ${peerId}: ${subscriptions.length} change(s)`);

    context.callbacks.onSubscriptionChange?.(peerId, subscriptions);
  }

  // libp2p's built-in ReconnectQueue gives up after its retry budget and strips the
  // keep-alive tag (see the keep-alive memory note). On `peer:reconnect-failure` for an
  // infra peer we re-dial on a steady cadence until the node stops or the peer reconnects;
  // a successful dial fires `peer:connect`, which re-applies the keep-alive tag so the
  // built-in queue covers the next drop.
  private rearmInfraPeer(node: LibP2pWithServices, peerId: PeerId): void {
    if (node.status !== 'started') {
      return;
    }

    const peerIdString = peerId.toString();
    node
      .dial(peerId)
      .then(() => {
        this.log.info(`Re-established infra peer ${peerIdString}`);
      })
      .catch((error: unknown) => {
        const retryInterval = P2P_CONFIG.keepAlive.rearmInterval;
        this.log.warn(
          `Infra reconnect dial failed for ${peerIdString}, retrying in ${retryInterval}ms: ${getErrorMessage(error)}`
        );
        setTimeout(() => {
          this.rearmInfraPeer(node, peerId);
        }, retryInterval);
      });
  }

  private reannounceSubscriptions(node: LibP2pWithServices, targetPeerId: string): void {
    try {
      const topics = node.services.pubsub.getTopics();

      if (topics.length === 0) {
        this.log.info(`No subscriptions to re-announce to ${targetPeerId}`);
        return;
      }

      this.log.info(`Re-announcing ${topics.length} subscription(s) to HAP ${targetPeerId}`);
      this.log.info(`Topics: ${topics.join(', ')}`);

      for (const topic of topics) {
        node.services.pubsub.unsubscribe(topic);
        node.services.pubsub.subscribe(topic);
        this.log.debug(`Re-subscribed to: ${topic}`);
      }

      this.log.info('Re-announced all subscriptions to HAP');

      setTimeout(() => {
        for (const topic of topics) {
          const subscribers = node.services.pubsub.getSubscribers(topic);
          const hapSubscriber = subscribers.find((peer) => peer.toString() === targetPeerId);
          this.log.info(
            `Topic '${topic}': ${subscribers.length} subscriber(s), HAP subscribed: ${
              hapSubscriber != null ? 'YES' : 'NO'
            }`
          );
        }
      }, P2P_CONFIG.reannounce.subscriptionVerifyDelay);
    } catch (error) {
      this.log.error('Error re-announcing subscriptions:', error);
    }
  }
}
